import React, { useEffect, useState, useRef, Component } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppContext, KycData, KycDocument, KycTier } from '../contexts/AppContext';
import { toast } from 'sonner';
import { User, Shield, Bell, CreditCard, Download, Save, Eye, EyeOff, Monitor, Smartphone, Globe, MapPin, Clock, LogOut, ShieldCheck, Lock, CheckCircle2, Camera, Upload, AlertTriangle, KeyRound, Mail, MessageSquare, Fingerprint, BellRing, QrCode, Copy, X, Trash2, Plus } from 'lucide-react';
import { formatDateTime, formatCurrency } from '../utils/formatters';
import { Modal } from '../components/ui/Modal';
export function Settings() {
  const {
    state,
    dispatch
  } = useAppContext();
  const {
    user
  } = state;
  const location = useLocation();
  const [activeSection, setActiveSection] = useState('profile');
  useEffect(() => {
    if (location.state && (location.state as any).activeSection) {
      setActiveSection((location.state as any).activeSection);
    }
  }, [location.state]);
  // Profile form
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleAvatarUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        dispatch({
          type: 'UPDATE_USER',
          payload: {
            avatar: reader.result as string
          }
        });
        toast.success('Profile picture updated');
      };
      reader.readAsDataURL(file);
    }
  };
  // Security
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [transactionPin, setTransactionPin] = useState('');
  const [twoFactor, setTwoFactor] = useState(false);
  // 2FA Method States
  const [twoFaAuthenticator, setTwoFaAuthenticator] = useState(false);
  const [twoFaHardwareKey, setTwoFaHardwareKey] = useState(false);
  const [twoFaSms, setTwoFaSms] = useState(false);
  const [twoFaEmail, setTwoFaEmail] = useState(false);
  const [twoFaBiometrics, setTwoFaBiometrics] = useState(false);
  const [twoFaPush, setTwoFaPush] = useState(false);
  // 2FA Setup Modals
  const [setupModal, setSetupModal] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [isSettingUp, setIsSettingUp] = useState(false);
  // Fake TOTP secret for authenticator setup
  const totpSecret = 'BVST-4K7X-9QR2-HNLP';
  // KYC Forms
  const [bvn, setBvn] = useState('');
  const [idType, setIdType] = useState('nin');
  const [idNumber, setIdNumber] = useState('');
  const [docType, setDocType] = useState('utility_bill');
  const [employmentStatus, setEmploymentStatus] = useState('employed');
  const [incomeRange, setIncomeRange] = useState('50k-200k');
  const [isVerifying, setIsVerifying] = useState(false);
  // Simulated Login Activities
  const loginActivities = [{
    id: 1,
    device: 'Chrome on MacOS',
    ip: '102.89.23.145',
    location: 'Lagos, Nigeria',
    date: new Date().toISOString(),
    status: 'Active',
    icon: Monitor
  }, {
    id: 2,
    device: 'Safari on iPhone 15',
    ip: '197.210.45.12',
    location: 'Abuja, Nigeria',
    date: new Date(Date.now() - 86400000).toISOString(),
    status: 'Success',
    icon: Smartphone
  }, {
    id: 3,
    device: 'Firefox on Windows 11',
    ip: '102.89.23.145',
    location: 'Lagos, Nigeria',
    date: new Date(Date.now() - 172800000).toISOString(),
    status: 'Success',
    icon: Monitor
  }];
  // Notifications
  const [emailNotif, setEmailNotif] = useState(true);
  const [smsNotif, setSmsNotif] = useState(true);
  const [pushNotif, setPushNotif] = useState(false);
  const [transactionAlerts, setTransactionAlerts] = useState(true);
  const [campaignUpdates, setCampaignUpdates] = useState(true);
  const [rentReminders, setRentReminders] = useState(true);
  // Bank accounts
  const [bankAccounts, setBankAccounts] = useState([{
    id: '1',
    bank: 'GTBank',
    accountNumber: '0123456789',
    accountName: 'Tunde Adeleke'
  }]);
  const [isAddBankOpen, setIsAddBankOpen] = useState(false);
  const [newBankName, setNewBankName] = useState('');
  const [newBankAccountNumber, setNewBankAccountNumber] = useState('');
  const [newBankAccountName, setNewBankAccountName] = useState('');
  const sections = [{
    id: 'profile',
    label: 'Profile',
    icon: User
  }, {
    id: 'kyc',
    label: 'KYC Verification',
    icon: ShieldCheck
  }, {
    id: 'security',
    label: 'Security',
    icon: Shield
  }, {
    id: 'notifications',
    label: 'Notifications',
    icon: Bell
  }, {
    id: 'bank-accounts',
    label: 'Bank Accounts',
    icon: CreditCard
  }, {
    id: 'data',
    label: 'Data & Export',
    icon: Download
  }];
  const handleSaveProfile = (e: React.FormEvent) => {
    e.preventDefault();
    toast.success('Profile updated successfully');
  };
  const handleChangePassword = (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    toast.success('Password changed successfully');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };
  const handleSetPin = (e: React.FormEvent) => {
    e.preventDefault();
    if (transactionPin.length !== 4 || !/^\d+$/.test(transactionPin)) {
      toast.error('PIN must be exactly 4 digits');
      return;
    }
    toast.success('Transaction PIN set successfully');
    setTransactionPin('');
  };
  // 2FA Setup Handlers
  const handle2FASetup = (method: string) => {
    setVerificationCode('');
    setSetupModal(method);
  };
  const handle2FAVerify = (method: string) => {
    if (verificationCode.length < 4) {
      toast.error('Please enter a valid verification code');
      return;
    }
    setIsSettingUp(true);
    setTimeout(() => {
      switch (method) {
        case 'authenticator':
          setTwoFaAuthenticator(true);
          break;
        case 'hardware_key':
          setTwoFaHardwareKey(true);
          break;
        case 'sms':
          setTwoFaSms(true);
          break;
        case 'email':
          setTwoFaEmail(true);
          break;
        case 'biometrics':
          setTwoFaBiometrics(true);
          break;
        case 'push':
          setTwoFaPush(true);
          break;
      }
      setTwoFactor(true);
      setIsSettingUp(false);
      setSetupModal(null);
      setVerificationCode('');
      toast.success('Two-factor method enabled successfully');
    }, 1500);
  };
  const handle2FADisable = (method: string) => {
    switch (method) {
      case 'authenticator':
        setTwoFaAuthenticator(false);
        break;
      case 'hardware_key':
        setTwoFaHardwareKey(false);
        break;
      case 'sms':
        setTwoFaSms(false);
        break;
      case 'email':
        setTwoFaEmail(false);
        break;
      case 'biometrics':
        setTwoFaBiometrics(false);
        break;
      case 'push':
        setTwoFaPush(false);
        break;
    }
    const anyEnabled = [method !== 'authenticator' && twoFaAuthenticator, method !== 'hardware_key' && twoFaHardwareKey, method !== 'sms' && twoFaSms, method !== 'email' && twoFaEmail, method !== 'biometrics' && twoFaBiometrics, method !== 'push' && twoFaPush].some(Boolean);
    if (!anyEnabled) setTwoFactor(false);
    toast.success('Two-factor method disabled');
  };
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };
  const handleVerifyTier1 = (e: React.FormEvent) => {
    e.preventDefault();
    if (bvn.length !== 11) {
      toast.error('BVN must be 11 digits');
      return;
    }
    setIsVerifying(true);
    setTimeout(() => {
      const newKyc: KycData = {
        ...(user?.kyc || {
          tier: 0,
          status: 'unverified',
          documents: [],
          dailyLimit: 10000
        }),
        tier: 1,
        status: 'verified',
        dailyLimit: 50000,
        tier1CompletedAt: new Date().toISOString(),
        documents: [...(user?.kyc?.documents || []), {
          type: 'bvn',
          status: 'verified',
          submittedAt: new Date().toISOString(),
          verifiedAt: new Date().toISOString(),
          value: `****${bvn.slice(-4)}`
        }]
      };
      dispatch({
        type: 'UPDATE_KYC',
        payload: newKyc
      });
      setIsVerifying(false);
      toast.success('Tier 1 Verification Successful');
    }, 2000);
  };
  const handleVerifyTier2 = (e: React.FormEvent) => {
    e.preventDefault();
    if (!idNumber) {
      toast.error('Please enter ID number');
      return;
    }
    setIsVerifying(true);
    setTimeout(() => {
      const newKyc: KycData = {
        ...(user?.kyc as KycData),
        tier: 2,
        dailyLimit: 200000,
        tier2CompletedAt: new Date().toISOString(),
        documents: [...(user?.kyc?.documents || []), {
          type: idType as any,
          status: 'verified',
          submittedAt: new Date().toISOString(),
          verifiedAt: new Date().toISOString(),
          value: `****${idNumber.slice(-4)}`
        }, {
          type: 'selfie',
          status: 'verified',
          submittedAt: new Date().toISOString(),
          verifiedAt: new Date().toISOString()
        }]
      };
      dispatch({
        type: 'UPDATE_KYC',
        payload: newKyc
      });
      setIsVerifying(false);
      toast.success('Tier 2 Verification Successful');
    }, 2000);
  };
  const handleVerifyTier3 = (e: React.FormEvent) => {
    e.preventDefault();
    setIsVerifying(true);
    setTimeout(() => {
      const newKyc: KycData = {
        ...(user?.kyc as KycData),
        tier: 3,
        dailyLimit: 999999999,
        tier3CompletedAt: new Date().toISOString(),
        documents: [...(user?.kyc?.documents || []), {
          type: docType as any,
          status: 'verified',
          submittedAt: new Date().toISOString(),
          verifiedAt: new Date().toISOString()
        }]
      };
      dispatch({
        type: 'UPDATE_KYC',
        payload: newKyc
      });
      setIsVerifying(false);
      toast.success('Tier 3 Verification Successful');
    }, 2000);
  };
  const handleAddBankAccount = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newBankName || !newBankAccountNumber || !newBankAccountName) {
      toast.error('Please fill in all fields');
      return;
    }
    if (newBankAccountNumber.length !== 10 || !/^\d+$/.test(newBankAccountNumber)) {
      toast.error('Account number must be exactly 10 digits');
      return;
    }
    const newAccount = {
      id: `bank_${Date.now()}`,
      bank: newBankName,
      accountNumber: newBankAccountNumber,
      accountName: newBankAccountName
    };
    setBankAccounts(prev => [...prev, newAccount]);
    setNewBankName('');
    setNewBankAccountNumber('');
    setNewBankAccountName('');
    setIsAddBankOpen(false);
    toast.success('Bank account added successfully');
  };
  const handleRemoveBankAccount = (id: string) => {
    if (bankAccounts.length <= 1) {
      toast.error('You must have at least one linked bank account');
      return;
    }
    setBankAccounts(prev => prev.filter(a => a.id !== id));
    toast.success('Bank account removed');
  };
  const handleExportData = () => {
    const {
      user,
      transactions,
      campaigns,
      transfers,
      properties
    } = state;
    const now = new Date();
    const dateStr = now.toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    const htmlLines = ['<!DOCTYPE html>', '<html><head><meta charset="utf-8">', '<title>Billvast Data Export</title>', '<style>{`', 'body { font-family: Arial, sans-serif; padding: 40px; color: #1A2E1A; max-width: 900px; margin: 0 auto; }', 'h1 { color: #006633; font-size: 24px; border-bottom: 3px solid #73C883; padding-bottom: 12px; }', 'h2 { color: #006633; font-size: 18px; margin-top: 32px; border-bottom: 1px solid #e5e7eb; padding-bottom: 8px; }', 'table { width: 100%; border-collapse: collapse; margin-top: 12px; font-size: 13px; }', 'th { background: #006633; color: white; padding: 10px 12px; text-align: left; font-weight: 600; }', 'td { padding: 8px 12px; border-bottom: 1px solid #e5e7eb; }', 'tr:nth-child(even) { background: #f9fafb; }', '.header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; }', '.header img { height: 40px; }', '.meta { color: #6B7280; font-size: 13px; }', '.section-count { color: #6B7280; font-size: 13px; margin-top: 4px; }', '.stamp { text-align: center; margin-top: 48px; padding-top: 24px; border-top: 2px solid #e5e7eb; color: #6B7280; font-size: 12px; }', '@media print { body { padding: 20px; } @page { margin: 1cm; } }', '`}</style></head><body>', '<div class="header">', '<div>', '<h1 style="border:none;padding:0;margin:0;">BILLVAST DATA EXPORT</h1>', '<p class="meta">Generated on ' + dateStr + '</p>', '</div>', '<img src="https://cdn.magicpatterns.com/uploads/4AEfCpc54BiEMtMYwDkd9o/Billvast_logo.png" alt="Billvast" />', '</div>', '', '<h2>Profile Information</h2>', '<table>', '<tr><th>Field</th><th>Value</th></tr>', '<tr><td>Name</td><td>' + (user?.name || 'N/A') + '</td></tr>', '<tr><td>Email</td><td>' + (user?.email || 'N/A') + '</td></tr>', '<tr><td>Phone</td><td>' + (user?.phone || 'N/A') + '</td></tr>', '<tr><td>KYC Tier</td><td>Tier ' + (user?.kyc?.tier || 0) + '</td></tr>', '<tr><td>Wallet Balance</td><td>' + formatCurrency(user?.walletBalance || 0) + '</td></tr>', '</table>', '', '<h2>Transactions</h2>', '<p class="section-count">' + transactions.length + ' total transactions</p>', '<table>', '<tr><th>Date</th><th>Title</th><th>Type</th><th>Amount</th><th>Status</th><th>Reference</th></tr>'];
    transactions.forEach(tx => {
      htmlLines.push('<tr>' + '<td>' + new Date(tx.date).toLocaleDateString('en-NG') + '</td>' + '<td>' + tx.title + '</td>' + '<td>' + tx.type + '</td>' + '<td>' + formatCurrency(tx.amount) + '</td>' + '<td>' + tx.status + '</td>' + '<td>' + (tx.reference || '-') + '</td>' + '</tr>');
    });
    htmlLines.push('</table>');
    htmlLines.push('');
    htmlLines.push('<h2>Campaigns</h2>');
    htmlLines.push('<p class="section-count">' + campaigns.length + ' campaigns</p>');
    htmlLines.push('<table>');
    htmlLines.push('<tr><th>Title</th><th>Category</th><th>Goal</th><th>Raised</th><th>Status</th></tr>');
    campaigns.forEach(c => {
      htmlLines.push('<tr>' + '<td>' + c.title + '</td>' + '<td>' + c.category + '</td>' + '<td>' + formatCurrency(c.goalAmount) + '</td>' + '<td>' + formatCurrency(c.raisedAmount) + '</td>' + '<td>' + c.status + '</td>' + '</tr>');
    });
    htmlLines.push('</table>');
    if (transfers && transfers.length > 0) {
      htmlLines.push('<h2>Transfers</h2>');
      htmlLines.push('<p class="section-count">' + transfers.length + ' transfers</p>');
      htmlLines.push('<table>');
      htmlLines.push('<tr><th>Date</th><th>Recipient</th><th>Amount</th><th>Status</th></tr>');
      transfers.forEach((t: any) => {
        htmlLines.push('<tr>' + '<td>' + new Date(t.date).toLocaleDateString('en-NG') + '</td>' + '<td>' + (t.recipientName || t.recipient || '-') + '</td>' + '<td>' + formatCurrency(t.amount) + '</td>' + '<td>' + t.status + '</td>' + '</tr>');
      });
      htmlLines.push('</table>');
    }
    htmlLines.push('');
    htmlLines.push('<div class="stamp">');
    htmlLines.push('<p>This document was generated by Billvast Fintech Platform</p>');
    htmlLines.push('<p>Lagos, Nigeria &bull; ' + dateStr + '</p>');
    htmlLines.push('</div>');
    htmlLines.push('</body></html>');
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(htmlLines.join('\n'));
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
      }, 500);
    }
    toast.success('Data export PDF generated');
  };
  const ToggleSwitch = ({
    enabled,
    onChange
  }: {
    enabled: boolean;
    onChange: (v: boolean) => void;
  }) => <button type="button" onClick={() => onChange(!enabled)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${enabled ? 'bg-brand-primary' : 'bg-gray-200'}`} data-id="element-1370">
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-1371" />
    </button>;
  const kycTier = user?.kyc?.tier || 0;
  return <div className="space-y-6" data-id="element-1372">
      <div data-id="element-1373">
        <h1 className="text-2xl font-bold text-brand-text-dark" data-id="element-1374">Settings</h1>
        <p className="text-brand-text-light mt-1" data-id="element-1375">
          Manage your account preferences and security.
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6" data-id="element-1376">
        {/* Sidebar Nav */}
        <div className="lg:w-56 shrink-0" data-id="element-1377">
          <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0" data-id="element-1378">
            {sections.map(section => {
            const Icon = section.icon;
            return <button key={section.id} onClick={() => setActiveSection(section.id)} className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${activeSection === section.id ? 'bg-brand-primary/10 text-brand-primary' : 'text-brand-text-light hover:bg-gray-50 hover:text-brand-text-dark'}`} data-id="element-1379">
                  <Icon size={18} data-id="element-1380" />
                  {section.label}
                </button>;
          })}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0" data-id="element-1381">
          {/* Profile Section */}
          {activeSection === 'profile' && <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-id="element-1382">
              <h2 className="text-lg font-semibold text-brand-text-dark mb-6" data-id="element-1383">
                Profile Information
              </h2>
              <form onSubmit={handleSaveProfile} className="space-y-5 max-w-lg" data-id="element-1384">
                <div className="flex items-center gap-4 mb-6" data-id="element-1385">
                  <div className="relative group cursor-pointer" onClick={() => fileInputRef.current?.click()} data-id="element-1386">
                    <img src={user?.avatar || 'https://ui-avatars.com/api/?name=User&background=73C883&color=fff'} alt="Avatar" className="w-16 h-16 rounded-full object-cover border-2 border-transparent group-hover:border-brand-primary transition-colors" data-id="element-1387" />
                    <div className="absolute bottom-0 right-0 w-6 h-6 bg-brand-primary text-white rounded-full flex items-center justify-center border-2 border-white shadow-sm opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1388">
                      <Camera size={12} data-id="element-1389" />
                    </div>
                    <input type="file" ref={fileInputRef} onChange={handleAvatarUpload} accept="image/*" className="hidden" data-id="element-1390" />
                  </div>
                  <div data-id="element-1391">
                    <p className="font-semibold text-brand-text-dark" data-id="element-1392">
                      {user?.name}
                    </p>
                    <p className="text-sm text-brand-text-light" data-id="element-1393">
                      {user?.email}
                    </p>
                  </div>
                </div>

                <div data-id="element-1394">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1395">
                    Full Name
                  </label>
                  <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none" data-id="element-1396" />
                </div>
                <div data-id="element-1397">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1398">
                    Email Address
                  </label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none" data-id="element-1399" />
                </div>
                <div data-id="element-1400">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1401">
                    Phone Number
                  </label>
                  <input type="tel" value={phone} onChange={e => setPhone(e.target.value)} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none" data-id="element-1402" />
                </div>
                <button type="submit" className="inline-flex items-center gap-2 px-6 py-2.5 bg-brand-primary text-white rounded-xl font-medium hover:bg-brand-primary-medium transition-colors" data-id="element-1403">
                  <Save size={18} data-id="element-1404" /> Save Changes
                </button>
              </form>
            </div>}

          {/* KYC Section */}
          {activeSection === 'kyc' && <div className="space-y-6" data-id="element-1405">
              {/* Header Card */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-id="element-1406">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8" data-id="element-1407">
                  <div data-id="element-1408">
                    <h2 className="text-xl font-bold text-brand-text-dark" data-id="element-1409">
                      KYC Verification
                    </h2>
                    <p className="text-sm text-brand-text-light mt-1" data-id="element-1410">
                      Upgrade your tier to unlock higher transaction limits.
                    </p>
                  </div>
                  <div className="px-4 py-2 bg-gray-50 rounded-xl border border-gray-100 text-center" data-id="element-1411">
                    <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold mb-1" data-id="element-1412">
                      Current Limit
                    </p>
                    <p className="text-lg font-bold text-brand-primary-dark" data-id="element-1413">
                      {kycTier === 3 ? 'Unlimited' : `₦${(user?.kyc?.dailyLimit || 10000).toLocaleString()}/day`}
                    </p>
                  </div>
                </div>

                {/* Stepper */}
                <div className="relative flex justify-between items-center max-w-2xl mx-auto px-4 sm:px-8 mb-4" data-id="element-1414">
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-gray-100 -z-10 rounded-full" data-id="element-1415"></div>
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 h-1 bg-brand-primary -z-10 rounded-full transition-all duration-500" style={{
                width: `${kycTier / 3 * 100}%`
              }} data-id="element-1416"></div>

                  {[0, 1, 2, 3].map(tier => {
                const isCompleted = kycTier >= tier;
                const isCurrent = kycTier === tier - 1;
                const labels = ['Unverified', 'Basic', 'Standard', 'Premium'];
                return <div key={tier} className="flex flex-col items-center gap-2 relative bg-white" data-id="element-1417">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors duration-300 ${isCompleted ? 'bg-brand-primary text-white' : isCurrent ? 'bg-white border-2 border-brand-primary text-brand-primary ring-4 ring-brand-primary/20' : 'bg-gray-100 text-gray-400'}`} data-id="element-1418">
                          {isCompleted && tier > 0 ? <CheckCircle2 size={16} data-id="element-1419" /> : tier}
                        </div>
                        <span className={`absolute top-10 text-xs font-semibold whitespace-nowrap ${isCompleted || isCurrent ? 'text-brand-text-dark' : 'text-gray-400'}`} data-id="element-1420">
                          {labels[tier]}
                        </span>
                      </div>;
              })}
                </div>
              </div>

              {/* Tier 1 Card */}
              <motion.div layout className={`rounded-2xl border transition-all duration-300 overflow-hidden ${kycTier >= 1 ? 'bg-green-50/50 border-green-200' : kycTier === 0 ? 'bg-white border-brand-primary shadow-md' : 'bg-gray-50 border-gray-200 opacity-75'}`} data-id="element-1421">
                <div className="p-6" data-id="element-1422">
                  <div className="flex items-start justify-between mb-4" data-id="element-1423">
                    <div data-id="element-1424">
                      <div className="flex items-center gap-2 mb-1" data-id="element-1425">
                        <h3 className="text-lg font-bold text-brand-text-dark" data-id="element-1426">
                          Tier 1: Basic
                        </h3>
                        {kycTier >= 1 && <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1" data-id="element-1427">
                            <CheckCircle2 size={12} data-id="element-1428" /> Verified
                          </span>}
                      </div>
                      <p className="text-sm text-gray-600" data-id="element-1429">
                        Limit: ₦50,000/day • Requirements: Phone & BVN
                      </p>
                    </div>
                  </div>

                  {kycTier >= 1 ? <div className="bg-white/60 rounded-xl p-4 border border-green-100 flex items-center justify-between" data-id="element-1430">
                      <div className="flex items-center gap-3" data-id="element-1431">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-green-600" data-id="element-1432">
                          <ShieldCheck size={20} data-id="element-1433" />
                        </div>
                        <div data-id="element-1434">
                          <p className="font-medium text-brand-text-dark" data-id="element-1435">
                            BVN Verified
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-1436">
                            Completed on{' '}
                            {formatDateTime(user?.kyc?.tier1CompletedAt || new Date().toISOString())}
                          </p>
                        </div>
                      </div>
                      <span className="font-mono text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-lg" data-id="element-1437">
                        {user?.kyc?.documents?.find(d => d.type === 'bvn')?.value || '****5678'}
                      </span>
                    </div> : kycTier === 0 ? <form onSubmit={handleVerifyTier1} className="space-y-4 mt-4" data-id="element-1438">
                      <div data-id="element-1439">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1440">
                          Bank Verification Number (BVN)
                        </label>
                        <input type="text" maxLength={11} value={bvn} onChange={e => setBvn(e.target.value.replace(/\D/g, ''))} placeholder="Enter 11-digit BVN" className="w-full max-w-md px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none font-mono" data-id="element-1441" />
                      </div>
                      <button type="submit" disabled={isVerifying} className="px-6 py-2.5 bg-brand-primary hover:bg-brand-primary-dark text-white font-semibold rounded-xl transition-colors disabled:opacity-70 flex items-center gap-2" data-id="element-1442">
                        {isVerifying ? <>
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1443" />{' '}
                            Verifying...
                          </> : 'Verify BVN'}
                      </button>
                    </form> : null}
                </div>
              </motion.div>

              {/* Tier 2 Card */}
              <motion.div layout className={`rounded-2xl border transition-all duration-300 overflow-hidden ${kycTier >= 2 ? 'bg-green-50/50 border-green-200' : kycTier === 1 ? 'bg-white border-brand-primary shadow-md' : 'bg-gray-50 border-gray-200'}`} data-id="element-1444">
                <div className="p-6" data-id="element-1445">
                  <div className="flex items-start justify-between mb-4" data-id="element-1446">
                    <div data-id="element-1447">
                      <div className="flex items-center gap-2 mb-1" data-id="element-1448">
                        <h3 className="text-lg font-bold text-brand-text-dark" data-id="element-1449">
                          Tier 2: Standard
                        </h3>
                        {kycTier >= 2 && <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1" data-id="element-1450">
                            <CheckCircle2 size={12} data-id="element-1451" /> Verified
                          </span>}
                        {kycTier < 1 && <span className="bg-gray-200 text-gray-600 text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1" data-id="element-1452">
                            <Lock size={12} data-id="element-1453" /> Locked
                          </span>}
                      </div>
                      <p className="text-sm text-gray-600" data-id="element-1454">
                        Limit: ₦200,000/day • Requirements: Government ID &
                        Selfie
                      </p>
                    </div>
                  </div>

                  {kycTier >= 2 ? <div className="bg-white/60 rounded-xl p-4 border border-green-100 flex items-center justify-between" data-id="element-1455">
                      <div className="flex items-center gap-3" data-id="element-1456">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-green-600" data-id="element-1457">
                          <ShieldCheck size={20} data-id="element-1458" />
                        </div>
                        <div data-id="element-1459">
                          <p className="font-medium text-brand-text-dark" data-id="element-1460">
                            ID & Selfie Verified
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-1461">
                            Completed on{' '}
                            {formatDateTime(user?.kyc?.tier2CompletedAt || new Date().toISOString())}
                          </p>
                        </div>
                      </div>
                    </div> : kycTier === 1 ? <form onSubmit={handleVerifyTier2} className="space-y-5 mt-4 max-w-md" data-id="element-1462">
                      <div data-id="element-1463">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1464">
                          ID Type
                        </label>
                        <select value={idType} onChange={e => setIdType(e.target.value)} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none bg-white" data-id="element-1465">
                          <option value="nin" data-id="element-1466">NIN Slip</option>
                          <option value="voters_card" data-id="element-1467">Voter's Card</option>
                          <option value="drivers_license" data-id="element-1468">
                            Driver's License
                          </option>
                          <option value="passport" data-id="element-1469">
                            International Passport
                          </option>
                        </select>
                      </div>
                      <div data-id="element-1470">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1471">
                          ID Number
                        </label>
                        <input type="text" value={idNumber} onChange={e => setIdNumber(e.target.value)} placeholder="Enter ID Number" className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none font-mono uppercase" data-id="element-1472" />
                      </div>
                      <div data-id="element-1473">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1474">
                          Selfie Verification
                        </label>
                        <button type="button" onClick={() => toast.success('Selfie captured successfully')} className="w-full py-8 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-brand-primary hover:text-brand-primary hover:bg-brand-primary/5 transition-colors flex flex-col items-center justify-center gap-2" data-id="element-1475">
                          <Camera size={24} data-id="element-1476" />
                          <span className="font-medium" data-id="element-1477">Take a Selfie</span>
                        </button>
                      </div>
                      <button type="submit" disabled={isVerifying} className="w-full py-3 bg-brand-primary hover:bg-brand-primary-dark text-white font-semibold rounded-xl transition-colors disabled:opacity-70 flex items-center justify-center gap-2 shadow-md" data-id="element-1478">
                        {isVerifying ? <>
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1479" />{' '}
                            Verifying...
                          </> : 'Submit for Verification'}
                      </button>
                    </form> : <div className="mt-4 p-4 bg-gray-100 rounded-xl flex items-center gap-3 text-gray-500 text-sm" data-id="element-1480">
                      <Lock size={18} data-id="element-1481" /> Complete Tier 1 to unlock this tier.
                    </div>}
                </div>
              </motion.div>

              {/* Tier 3 Card */}
              <motion.div layout className={`rounded-2xl border transition-all duration-300 overflow-hidden ${kycTier >= 3 ? 'bg-green-50/50 border-green-200' : kycTier === 2 ? 'bg-white border-brand-primary shadow-md' : 'bg-gray-50 border-gray-200'}`} data-id="element-1482">
                <div className="p-6" data-id="element-1483">
                  <div className="flex items-start justify-between mb-4" data-id="element-1484">
                    <div data-id="element-1485">
                      <div className="flex items-center gap-2 mb-1" data-id="element-1486">
                        <h3 className="text-lg font-bold text-brand-text-dark" data-id="element-1487">
                          Tier 3: Premium
                        </h3>
                        {kycTier >= 3 && <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1" data-id="element-1488">
                            <CheckCircle2 size={12} data-id="element-1489" /> Verified
                          </span>}
                        {kycTier < 2 && <span className="bg-gray-200 text-gray-600 text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1" data-id="element-1490">
                            <Lock size={12} data-id="element-1491" /> Locked
                          </span>}
                      </div>
                      <p className="text-sm text-gray-600" data-id="element-1492">
                        Limit: Unlimited • Requirements: Proof of Address &
                        Income
                      </p>
                    </div>
                  </div>

                  {kycTier >= 3 ? <div className="bg-white/60 rounded-xl p-4 border border-green-100 flex items-center justify-between" data-id="element-1493">
                      <div className="flex items-center gap-3" data-id="element-1494">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center text-green-600" data-id="element-1495">
                          <ShieldCheck size={20} data-id="element-1496" />
                        </div>
                        <div data-id="element-1497">
                          <p className="font-medium text-brand-text-dark" data-id="element-1498">
                            Fully Verified
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-1499">
                            Completed on{' '}
                            {formatDateTime(user?.kyc?.tier3CompletedAt || new Date().toISOString())}
                          </p>
                        </div>
                      </div>
                    </div> : kycTier === 2 ? <form onSubmit={handleVerifyTier3} className="space-y-5 mt-4 max-w-md" data-id="element-1500">
                      <div data-id="element-1501">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1502">
                          Proof of Address
                        </label>
                        <select value={docType} onChange={e => setDocType(e.target.value)} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none bg-white mb-3" data-id="element-1503">
                          <option value="utility_bill" data-id="element-1504">
                            Utility Bill (Last 3 months)
                          </option>
                          <option value="bank_statement" data-id="element-1505">Bank Statement</option>
                          <option value="tenancy_agreement" data-id="element-1506">
                            Tenancy Agreement
                          </option>
                        </select>
                        <button type="button" onClick={() => toast.success('Document uploaded successfully')} className="w-full py-6 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-brand-primary hover:text-brand-primary hover:bg-brand-primary/5 transition-colors flex flex-col items-center justify-center gap-2" data-id="element-1507">
                          <Upload size={24} data-id="element-1508" />
                          <span className="font-medium" data-id="element-1509">Upload Document</span>
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-4" data-id="element-1510">
                        <div data-id="element-1511">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1512">
                            Employment
                          </label>
                          <select value={employmentStatus} onChange={e => setEmploymentStatus(e.target.value)} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none bg-white text-sm" data-id="element-1513">
                            <option value="employed" data-id="element-1514">Employed</option>
                            <option value="self_employed" data-id="element-1515">Self-Employed</option>
                            <option value="student" data-id="element-1516">Student</option>
                            <option value="retired" data-id="element-1517">Retired</option>
                            <option value="other" data-id="element-1518">Other</option>
                          </select>
                        </div>
                        <div data-id="element-1519">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1520">
                            Monthly Income
                          </label>
                          <select value={incomeRange} onChange={e => setIncomeRange(e.target.value)} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none bg-white text-sm" data-id="element-1521">
                            <option value="below_50k" data-id="element-1522">Below ₦50k</option>
                            <option value="50k-200k" data-id="element-1523">₦50k - ₦200k</option>
                            <option value="200k-500k" data-id="element-1524">₦200k - ₦500k</option>
                            <option value="500k-1m" data-id="element-1525">₦500k - ₦1M</option>
                            <option value="above_1m" data-id="element-1526">Above ₦1M</option>
                          </select>
                        </div>
                      </div>
                      <button type="submit" disabled={isVerifying} className="w-full py-3 bg-brand-primary hover:bg-brand-primary-dark text-white font-semibold rounded-xl transition-colors disabled:opacity-70 flex items-center justify-center gap-2 shadow-md" data-id="element-1527">
                        {isVerifying ? <>
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1528" />{' '}
                            Verifying...
                          </> : 'Submit for Verification'}
                      </button>
                    </form> : <div className="mt-4 p-4 bg-gray-100 rounded-xl flex items-center gap-3 text-gray-500 text-sm" data-id="element-1529">
                      <Lock size={18} data-id="element-1530" /> Complete Tier 2 to unlock this tier.
                    </div>}
                </div>
              </motion.div>
            </div>}

          {/* Security Section */}
          {activeSection === 'security' && <div className="space-y-6" data-id="element-1531">
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-id="element-1532">
                <h2 className="text-lg font-semibold text-brand-text-dark mb-6" data-id="element-1533">
                  Change Password
                </h2>
                <form onSubmit={handleChangePassword} className="space-y-4 max-w-lg" data-id="element-1534">
                  <div data-id="element-1535">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1536">
                      Current Password
                    </label>
                    <div className="relative" data-id="element-1537">
                      <input type={showPassword ? 'text' : 'password'} value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none pr-10" data-id="element-1538" />
                      <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-1539">
                        {showPassword ? <EyeOff size={18} data-id="element-1540" /> : <Eye size={18} data-id="element-1541" />}
                      </button>
                    </div>
                  </div>
                  <div data-id="element-1542">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1543">
                      New Password
                    </label>
                    <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none" data-id="element-1544" />
                  </div>
                  <div data-id="element-1545">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1546">
                      Confirm New Password
                    </label>
                    <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none" data-id="element-1547" />
                  </div>
                  <button type="submit" className="inline-flex items-center gap-2 px-6 py-2.5 bg-brand-primary text-white rounded-xl font-medium hover:bg-brand-primary-medium transition-colors" data-id="element-1548">
                    Update Password
                  </button>
                </form>
              </div>

              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-id="element-1549">
                <h2 className="text-lg font-semibold text-brand-text-dark mb-6" data-id="element-1550">
                  Transaction PIN
                </h2>
                <form onSubmit={handleSetPin} className="space-y-4 max-w-lg" data-id="element-1551">
                  <div data-id="element-1552">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1553">
                      Set 4-Digit PIN
                    </label>
                    <input type="password" value={transactionPin} onChange={e => setTransactionPin(e.target.value.slice(0, 4))} maxLength={4} placeholder="••••" className="w-32 px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none text-center text-xl tracking-widest" data-id="element-1554" />
                  </div>
                  <button type="submit" className="inline-flex items-center gap-2 px-6 py-2.5 bg-brand-primary text-white rounded-xl font-medium hover:bg-brand-primary-medium transition-colors" data-id="element-1555">
                    Set PIN
                  </button>
                </form>
              </div>

              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-id="element-1556">
                <div className="mb-6" data-id="element-1557">
                  <div className="flex items-center justify-between" data-id="element-1558">
                    <div data-id="element-1559">
                      <h2 className="text-lg font-semibold text-brand-text-dark" data-id="element-1560">
                        Two-Factor Authentication
                      </h2>
                      <p className="text-sm text-brand-text-light mt-1" data-id="element-1561">
                        Add extra layers of security to your account with
                        multiple verification methods.
                      </p>
                    </div>
                    {twoFactor && <span className="flex items-center gap-1.5 text-xs font-bold bg-green-100 text-green-700 px-3 py-1.5 rounded-full" data-id="element-1562">
                        <ShieldCheck size={14} data-id="element-1563" /> Active
                      </span>}
                  </div>
                </div>

                <div className="space-y-4" data-id="element-1564">
                  {/* Authenticator App */}
                  <div className={`p-4 rounded-xl border transition-all ${twoFaAuthenticator ? 'bg-green-50/50 border-green-200' : 'bg-gray-50 border-gray-100'}`} data-id="element-1565">
                    <div className="flex items-center justify-between" data-id="element-1566">
                      <div className="flex items-center gap-4" data-id="element-1567">
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${twoFaAuthenticator ? 'bg-green-100 text-green-600' : 'bg-white text-gray-500 shadow-sm'}`} data-id="element-1568">
                          <QrCode size={22} data-id="element-1569" />
                        </div>
                        <div data-id="element-1570">
                          <div className="flex items-center gap-2" data-id="element-1571">
                            <p className="font-semibold text-brand-text-dark" data-id="element-1572">
                              Authenticator App
                            </p>
                            {twoFaAuthenticator && <CheckCircle2 size={16} className="text-green-500" data-id="element-1573" />}
                          </div>
                          <p className="text-xs text-brand-text-light mt-0.5" data-id="element-1574">
                            Google Authenticator, Authy, or Microsoft
                            Authenticator
                          </p>
                        </div>
                      </div>
                      {twoFaAuthenticator ? <button onClick={() => handle2FADisable('authenticator')} className="text-xs font-semibold text-red-500 hover:text-red-700 px-3 py-1.5 rounded-lg hover:bg-red-50 transition-colors" data-id="element-1575">
                          Disable
                        </button> : <button onClick={() => handle2FASetup('authenticator')} className="text-xs font-semibold text-brand-primary hover:text-brand-primary-dark px-3 py-1.5 rounded-lg hover:bg-brand-primary/10 transition-colors" data-id="element-1576">
                          Set Up
                        </button>}
                    </div>
                  </div>

                  {/* Hardware Key */}
                  <div className={`p-4 rounded-xl border transition-all ${twoFaHardwareKey ? 'bg-green-50/50 border-green-200' : 'bg-gray-50 border-gray-100'}`} data-id="element-1577">
                    <div className="flex items-center justify-between" data-id="element-1578">
                      <div className="flex items-center gap-4" data-id="element-1579">
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${twoFaHardwareKey ? 'bg-green-100 text-green-600' : 'bg-white text-gray-500 shadow-sm'}`} data-id="element-1580">
                          <KeyRound size={22} data-id="element-1581" />
                        </div>
                        <div data-id="element-1582">
                          <div className="flex items-center gap-2" data-id="element-1583">
                            <p className="font-semibold text-brand-text-dark" data-id="element-1584">
                              Hardware Key
                            </p>
                            <span className="text-[10px] font-bold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded" data-id="element-1585">
                              FIDO U2F
                            </span>
                            {twoFaHardwareKey && <CheckCircle2 size={16} className="text-green-500" data-id="element-1586" />}
                          </div>
                          <p className="text-xs text-brand-text-light mt-0.5" data-id="element-1587">
                            YubiKey, Titan Security Key, or other FIDO2 devices
                          </p>
                        </div>
                      </div>
                      {twoFaHardwareKey ? <button onClick={() => handle2FADisable('hardware_key')} className="text-xs font-semibold text-red-500 hover:text-red-700 px-3 py-1.5 rounded-lg hover:bg-red-50 transition-colors" data-id="element-1588">
                          Remove
                        </button> : <button onClick={() => handle2FASetup('hardware_key')} className="text-xs font-semibold text-brand-primary hover:text-brand-primary-dark px-3 py-1.5 rounded-lg hover:bg-brand-primary/10 transition-colors" data-id="element-1589">
                          Register
                        </button>}
                    </div>
                  </div>

                  {/* SMS Code */}
                  <div className={`p-4 rounded-xl border transition-all ${twoFaSms ? 'bg-green-50/50 border-green-200' : 'bg-gray-50 border-gray-100'}`} data-id="element-1590">
                    <div className="flex items-center justify-between" data-id="element-1591">
                      <div className="flex items-center gap-4" data-id="element-1592">
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${twoFaSms ? 'bg-green-100 text-green-600' : 'bg-white text-gray-500 shadow-sm'}`} data-id="element-1593">
                          <MessageSquare size={22} data-id="element-1594" />
                        </div>
                        <div data-id="element-1595">
                          <div className="flex items-center gap-2" data-id="element-1596">
                            <p className="font-semibold text-brand-text-dark" data-id="element-1597">
                              SMS Code
                            </p>
                            {twoFaSms && <CheckCircle2 size={16} className="text-green-500" data-id="element-1598" />}
                          </div>
                          <p className="text-xs text-brand-text-light mt-0.5" data-id="element-1599">
                            Receive a one-time code via text message to{' '}
                            {user?.phone || 'your phone'}
                          </p>
                        </div>
                      </div>
                      {twoFaSms ? <button onClick={() => handle2FADisable('sms')} className="text-xs font-semibold text-red-500 hover:text-red-700 px-3 py-1.5 rounded-lg hover:bg-red-50 transition-colors" data-id="element-1600">
                          Disable
                        </button> : <button onClick={() => handle2FASetup('sms')} className="text-xs font-semibold text-brand-primary hover:text-brand-primary-dark px-3 py-1.5 rounded-lg hover:bg-brand-primary/10 transition-colors" data-id="element-1601">
                          Enable
                        </button>}
                    </div>
                  </div>

                  {/* Email Code */}
                  <div className={`p-4 rounded-xl border transition-all ${twoFaEmail ? 'bg-green-50/50 border-green-200' : 'bg-gray-50 border-gray-100'}`} data-id="element-1602">
                    <div className="flex items-center justify-between" data-id="element-1603">
                      <div className="flex items-center gap-4" data-id="element-1604">
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${twoFaEmail ? 'bg-green-100 text-green-600' : 'bg-white text-gray-500 shadow-sm'}`} data-id="element-1605">
                          <Mail size={22} data-id="element-1606" />
                        </div>
                        <div data-id="element-1607">
                          <div className="flex items-center gap-2" data-id="element-1608">
                            <p className="font-semibold text-brand-text-dark" data-id="element-1609">
                              Email Code
                            </p>
                            {twoFaEmail && <CheckCircle2 size={16} className="text-green-500" data-id="element-1610" />}
                          </div>
                          <p className="text-xs text-brand-text-light mt-0.5" data-id="element-1611">
                            Receive a one-time code via email to{' '}
                            {user?.email || 'your email'}
                          </p>
                        </div>
                      </div>
                      {twoFaEmail ? <button onClick={() => handle2FADisable('email')} className="text-xs font-semibold text-red-500 hover:text-red-700 px-3 py-1.5 rounded-lg hover:bg-red-50 transition-colors" data-id="element-1612">
                          Disable
                        </button> : <button onClick={() => handle2FASetup('email')} className="text-xs font-semibold text-brand-primary hover:text-brand-primary-dark px-3 py-1.5 rounded-lg hover:bg-brand-primary/10 transition-colors" data-id="element-1613">
                          Enable
                        </button>}
                    </div>
                  </div>

                  {/* Biometrics */}
                  <div className={`p-4 rounded-xl border transition-all ${twoFaBiometrics ? 'bg-green-50/50 border-green-200' : 'bg-gray-50 border-gray-100'}`} data-id="element-1614">
                    <div className="flex items-center justify-between" data-id="element-1615">
                      <div className="flex items-center gap-4" data-id="element-1616">
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${twoFaBiometrics ? 'bg-green-100 text-green-600' : 'bg-white text-gray-500 shadow-sm'}`} data-id="element-1617">
                          <Fingerprint size={22} data-id="element-1618" />
                        </div>
                        <div data-id="element-1619">
                          <div className="flex items-center gap-2" data-id="element-1620">
                            <p className="font-semibold text-brand-text-dark" data-id="element-1621">
                              Biometrics
                            </p>
                            {twoFaBiometrics && <CheckCircle2 size={16} className="text-green-500" data-id="element-1622" />}
                          </div>
                          <p className="text-xs text-brand-text-light mt-0.5" data-id="element-1623">
                            Face ID, Touch ID, or fingerprint on supported
                            devices
                          </p>
                        </div>
                      </div>
                      {twoFaBiometrics ? <button onClick={() => handle2FADisable('biometrics')} className="text-xs font-semibold text-red-500 hover:text-red-700 px-3 py-1.5 rounded-lg hover:bg-red-50 transition-colors" data-id="element-1624">
                          Disable
                        </button> : <button onClick={() => handle2FASetup('biometrics')} className="text-xs font-semibold text-brand-primary hover:text-brand-primary-dark px-3 py-1.5 rounded-lg hover:bg-brand-primary/10 transition-colors" data-id="element-1625">
                          Register
                        </button>}
                    </div>
                  </div>

                  {/* Push Notifications */}
                  <div className={`p-4 rounded-xl border transition-all ${twoFaPush ? 'bg-green-50/50 border-green-200' : 'bg-gray-50 border-gray-100'}`} data-id="element-1626">
                    <div className="flex items-center justify-between" data-id="element-1627">
                      <div className="flex items-center gap-4" data-id="element-1628">
                        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${twoFaPush ? 'bg-green-100 text-green-600' : 'bg-white text-gray-500 shadow-sm'}`} data-id="element-1629">
                          <BellRing size={22} data-id="element-1630" />
                        </div>
                        <div data-id="element-1631">
                          <div className="flex items-center gap-2" data-id="element-1632">
                            <p className="font-semibold text-brand-text-dark" data-id="element-1633">
                              Push Notifications
                            </p>
                            {twoFaPush && <CheckCircle2 size={16} className="text-green-500" data-id="element-1634" />}
                          </div>
                          <p className="text-xs text-brand-text-light mt-0.5" data-id="element-1635">
                            Approve login requests via the Billvast mobile app
                          </p>
                        </div>
                      </div>
                      {twoFaPush ? <button onClick={() => handle2FADisable('push')} className="text-xs font-semibold text-red-500 hover:text-red-700 px-3 py-1.5 rounded-lg hover:bg-red-50 transition-colors" data-id="element-1636">
                          Disable
                        </button> : <button onClick={() => handle2FASetup('push')} className="text-xs font-semibold text-brand-primary hover:text-brand-primary-dark px-3 py-1.5 rounded-lg hover:bg-brand-primary/10 transition-colors" data-id="element-1637">
                          Enable
                        </button>}
                    </div>
                  </div>
                </div>

                {/* Security Recommendation */}
                {!twoFactor && <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3" data-id="element-1638">
                    <AlertTriangle size={20} className="text-amber-600 shrink-0 mt-0.5" data-id="element-1639" />
                    <div data-id="element-1640">
                      <p className="text-sm font-semibold text-amber-800" data-id="element-1641">
                        Recommended: Enable at least one 2FA method
                      </p>
                      <p className="text-xs text-amber-700 mt-1" data-id="element-1642">
                        Two-factor authentication significantly reduces the risk
                        of unauthorized access. We recommend using an
                        Authenticator App for the best security.
                      </p>
                    </div>
                  </div>}
              </div>

              {/* 2FA Setup Modals */}

              {/* Authenticator App Setup */}
              <Modal isOpen={setupModal === 'authenticator'} onClose={() => setSetupModal(null)} title="Set Up Authenticator App" maxWidth="max-w-md" data-id="element-1643">
                <div className="space-y-6" data-id="element-1644">
                  <div className="text-center" data-id="element-1645">
                    <p className="text-sm text-brand-text-light mb-4" data-id="element-1646">
                      Scan this QR code with your authenticator app (Google
                      Authenticator, Authy, etc.)
                    </p>
                    <div className="relative inline-block p-4 bg-white border-2 border-gray-100 rounded-2xl shadow-sm mb-4" data-id="element-1647">
                      <img src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(`otpauth://totp/Billvast:${user?.email}?secret=${totpSecret}&issuer=Billvast`)}`} alt="QR Code" className="w-40 h-40" data-id="element-1648" />
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-id="element-1649">
                        <div className="bg-white p-1 rounded-md shadow-sm border border-gray-100" data-id="element-1650">
                          <img src="https://cdn.magicpatterns.com/uploads/4AEfCpc54BiEMtMYwDkd9o/Billvast_logo.png" alt="Billvast" className="w-6 h-6 object-contain" data-id="element-1651" />
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-xl border border-gray-200 mb-4" data-id="element-1652">
                      <p className="text-xs text-gray-500 mb-1" data-id="element-1653">
                        Or enter this key manually:
                      </p>
                      <div className="flex items-center justify-center gap-2" data-id="element-1654">
                        <code className="text-sm font-bold text-brand-text-dark tracking-wider" data-id="element-1655">
                          {totpSecret}
                        </code>
                        <button onClick={() => copyToClipboard(totpSecret)} className="p-1 text-gray-400 hover:text-brand-primary transition-colors" data-id="element-1656">
                          <Copy size={14} data-id="element-1657" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div data-id="element-1658">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1659">
                      Enter the 6-digit code from your app
                    </label>
                    <input type="text" maxLength={6} value={verificationCode} onChange={e => setVerificationCode(e.target.value.replace(/\D/g, ''))} placeholder="000000" className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none text-center text-2xl font-bold tracking-[0.5em] font-mono" data-id="element-1660" />
                  </div>
                  <button onClick={() => handle2FAVerify('authenticator')} disabled={isSettingUp || verificationCode.length < 6} className="w-full py-3.5 bg-brand-primary hover:bg-brand-primary-dark text-white font-semibold rounded-xl transition-colors disabled:opacity-60 flex items-center justify-center gap-2" data-id="element-1661">
                    {isSettingUp ? <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1662" />{' '}
                        Verifying...
                      </> : 'Verify & Enable'}
                  </button>
                </div>
              </Modal>

              {/* Hardware Key Setup */}
              <Modal isOpen={setupModal === 'hardware_key'} onClose={() => setSetupModal(null)} title="Register Hardware Key" maxWidth="max-w-md" data-id="element-1663">
                <div className="space-y-6" data-id="element-1664">
                  <div className="text-center py-4" data-id="element-1665">
                    <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1666">
                      <KeyRound size={40} className="text-blue-600" data-id="element-1667" />
                    </div>
                    <h3 className="font-semibold text-brand-text-dark text-lg mb-2" data-id="element-1668">
                      Insert Your Security Key
                    </h3>
                    <p className="text-sm text-brand-text-light" data-id="element-1669">
                      Insert your FIDO U2F security key into a USB port, then
                      tap the button on your key when it blinks.
                    </p>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-xl border border-blue-100" data-id="element-1670">
                    <p className="text-xs text-blue-700 font-medium mb-2" data-id="element-1671">
                      Supported devices:
                    </p>
                    <ul className="text-xs text-blue-600 space-y-1" data-id="element-1672">
                      <li data-id="element-1673">• YubiKey 5 Series (USB-A, USB-C, NFC)</li>
                      <li data-id="element-1674">• Google Titan Security Key</li>
                      <li data-id="element-1675">• Feitian ePass FIDO</li>
                      <li data-id="element-1676">• Any FIDO2/WebAuthn compatible key</li>
                    </ul>
                  </div>
                  <div data-id="element-1677">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1678">
                      Name this key (optional)
                    </label>
                    <input type="text" value={verificationCode} onChange={e => setVerificationCode(e.target.value)} placeholder="e.g. My YubiKey" className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none" data-id="element-1679" />
                  </div>
                  <button onClick={() => {
                setVerificationCode('hardware');
                handle2FAVerify('hardware_key');
              }} disabled={isSettingUp} className="w-full py-3.5 bg-brand-primary hover:bg-brand-primary-dark text-white font-semibold rounded-xl transition-colors disabled:opacity-60 flex items-center justify-center gap-2" data-id="element-1680">
                    {isSettingUp ? <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1681" />{' '}
                        Detecting key...
                      </> : <>
                        <KeyRound size={18} data-id="element-1682" /> Register Security Key
                      </>}
                  </button>
                </div>
              </Modal>

              {/* SMS Code Setup */}
              <Modal isOpen={setupModal === 'sms'} onClose={() => setSetupModal(null)} title="Enable SMS Verification" maxWidth="max-w-md" data-id="element-1683">
                <div className="space-y-6" data-id="element-1684">
                  <div className="bg-gray-50 p-4 rounded-xl border border-gray-200" data-id="element-1685">
                    <p className="text-sm text-gray-600" data-id="element-1686">
                      We'll send a verification code to:
                    </p>
                    <p className="text-lg font-bold text-brand-text-dark mt-1" data-id="element-1687">
                      {user?.phone || '+234 XXX XXX XXXX'}
                    </p>
                  </div>
                  <div data-id="element-1688">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1689">
                      Enter the 6-digit code sent to your phone
                    </label>
                    <input type="text" maxLength={6} value={verificationCode} onChange={e => setVerificationCode(e.target.value.replace(/\D/g, ''))} placeholder="000000" className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none text-center text-2xl font-bold tracking-[0.5em] font-mono" data-id="element-1690" />
                    <button type="button" onClick={() => toast.success('Verification code sent!')} className="text-sm text-brand-primary font-medium mt-2 hover:underline" data-id="element-1691">
                      Send Code
                    </button>
                  </div>
                  <button onClick={() => handle2FAVerify('sms')} disabled={isSettingUp || verificationCode.length < 6} className="w-full py-3.5 bg-brand-primary hover:bg-brand-primary-dark text-white font-semibold rounded-xl transition-colors disabled:opacity-60 flex items-center justify-center gap-2" data-id="element-1692">
                    {isSettingUp ? <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1693" />{' '}
                        Verifying...
                      </> : 'Verify & Enable'}
                  </button>
                </div>
              </Modal>

              {/* Email Code Setup */}
              <Modal isOpen={setupModal === 'email'} onClose={() => setSetupModal(null)} title="Enable Email Verification" maxWidth="max-w-md" data-id="element-1694">
                <div className="space-y-6" data-id="element-1695">
                  <div className="bg-gray-50 p-4 rounded-xl border border-gray-200" data-id="element-1696">
                    <p className="text-sm text-gray-600" data-id="element-1697">
                      We'll send a verification code to:
                    </p>
                    <p className="text-lg font-bold text-brand-text-dark mt-1" data-id="element-1698">
                      {user?.email || 'your@email.com'}
                    </p>
                  </div>
                  <div data-id="element-1699">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1700">
                      Enter the 6-digit code sent to your email
                    </label>
                    <input type="text" maxLength={6} value={verificationCode} onChange={e => setVerificationCode(e.target.value.replace(/\D/g, ''))} placeholder="000000" className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none text-center text-2xl font-bold tracking-[0.5em] font-mono" data-id="element-1701" />
                    <button type="button" onClick={() => toast.success('Verification code sent to your email!')} className="text-sm text-brand-primary font-medium mt-2 hover:underline" data-id="element-1702">
                      Send Code
                    </button>
                  </div>
                  <button onClick={() => handle2FAVerify('email')} disabled={isSettingUp || verificationCode.length < 6} className="w-full py-3.5 bg-brand-primary hover:bg-brand-primary-dark text-white font-semibold rounded-xl transition-colors disabled:opacity-60 flex items-center justify-center gap-2" data-id="element-1703">
                    {isSettingUp ? <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1704" />{' '}
                        Verifying...
                      </> : 'Verify & Enable'}
                  </button>
                </div>
              </Modal>

              {/* Biometrics Setup */}
              <Modal isOpen={setupModal === 'biometrics'} onClose={() => setSetupModal(null)} title="Register Biometrics" maxWidth="max-w-md" data-id="element-1705">
                <div className="space-y-6" data-id="element-1706">
                  <div className="text-center py-4" data-id="element-1707">
                    <div className="w-20 h-20 bg-purple-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1708">
                      <Fingerprint size={40} className="text-purple-600" data-id="element-1709" />
                    </div>
                    <h3 className="font-semibold text-brand-text-dark text-lg mb-2" data-id="element-1710">
                      Register Your Biometrics
                    </h3>
                    <p className="text-sm text-brand-text-light" data-id="element-1711">
                      Use your device's built-in biometric sensor to register
                      Face ID, Touch ID, or fingerprint authentication.
                    </p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-xl border border-purple-100" data-id="element-1712">
                    <p className="text-xs text-purple-700 font-medium mb-2" data-id="element-1713">
                      Supported methods:
                    </p>
                    <div className="grid grid-cols-3 gap-2" data-id="element-1714">
                      <div className="text-center p-2 bg-white rounded-lg" data-id="element-1715">
                        <p className="text-lg mb-1" data-id="element-1716">👤</p>
                        <p className="text-[10px] font-medium text-gray-600" data-id="element-1717">
                          Face ID
                        </p>
                      </div>
                      <div className="text-center p-2 bg-white rounded-lg" data-id="element-1718">
                        <p className="text-lg mb-1" data-id="element-1719">👆</p>
                        <p className="text-[10px] font-medium text-gray-600" data-id="element-1720">
                          Touch ID
                        </p>
                      </div>
                      <div className="text-center p-2 bg-white rounded-lg" data-id="element-1721">
                        <p className="text-lg mb-1" data-id="element-1722">🔒</p>
                        <p className="text-[10px] font-medium text-gray-600" data-id="element-1723">
                          Windows Hello
                        </p>
                      </div>
                    </div>
                  </div>
                  <button onClick={() => {
                setVerificationCode('biometric');
                handle2FAVerify('biometrics');
              }} disabled={isSettingUp} className="w-full py-3.5 bg-brand-primary hover:bg-brand-primary-dark text-white font-semibold rounded-xl transition-colors disabled:opacity-60 flex items-center justify-center gap-2" data-id="element-1724">
                    {isSettingUp ? <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1725" />{' '}
                        Registering...
                      </> : <>
                        <Fingerprint size={18} data-id="element-1726" /> Register Biometrics
                      </>}
                  </button>
                </div>
              </Modal>

              {/* Push Notification Setup */}
              <Modal isOpen={setupModal === 'push'} onClose={() => setSetupModal(null)} title="Enable Push Notifications" maxWidth="max-w-md" data-id="element-1727">
                <div className="space-y-6" data-id="element-1728">
                  <div className="text-center py-4" data-id="element-1729">
                    <div className="w-20 h-20 bg-orange-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-1730">
                      <BellRing size={40} className="text-orange-600" data-id="element-1731" />
                    </div>
                    <h3 className="font-semibold text-brand-text-dark text-lg mb-2" data-id="element-1732">
                      Approve Logins via Push
                    </h3>
                    <p className="text-sm text-brand-text-light" data-id="element-1733">
                      When someone tries to log in to your account, you'll
                      receive a push notification on the Billvast app to approve
                      or deny the request.
                    </p>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-xl border border-orange-100" data-id="element-1734">
                    <div className="flex items-start gap-3" data-id="element-1735">
                      <Smartphone size={20} className="text-orange-600 shrink-0 mt-0.5" data-id="element-1736" />
                      <div data-id="element-1737">
                        <p className="text-sm font-semibold text-orange-800" data-id="element-1738">
                          Billvast Mobile App Required
                        </p>
                        <p className="text-xs text-orange-700 mt-1" data-id="element-1739">
                          Make sure you have the Billvast app installed and
                          you're signed in on your mobile device.
                        </p>
                      </div>
                    </div>
                  </div>
                  <button onClick={() => {
                setVerificationCode('push');
                handle2FAVerify('push');
              }} disabled={isSettingUp} className="w-full py-3.5 bg-brand-primary hover:bg-brand-primary-dark text-white font-semibold rounded-xl transition-colors disabled:opacity-60 flex items-center justify-center gap-2" data-id="element-1740">
                    {isSettingUp ? <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1741" />{' '}
                        Enabling...
                      </> : <>
                        <BellRing size={18} data-id="element-1742" /> Enable Push Notifications
                      </>}
                  </button>
                </div>
              </Modal>

              {/* Login Activity Card */}
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-id="element-1743">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6" data-id="element-1744">
                  <div data-id="element-1745">
                    <h2 className="text-lg font-semibold text-brand-text-dark" data-id="element-1746">
                      Login Activity
                    </h2>
                    <p className="text-sm text-brand-text-light mt-1" data-id="element-1747">
                      Recent devices and locations that have accessed your
                      account.
                    </p>
                  </div>
                  <button onClick={() => toast.success('Signed out of all other sessions')} className="inline-flex items-center gap-2 px-4 py-2 bg-gray-50 hover:bg-gray-100 text-brand-text-dark rounded-xl text-sm font-medium transition-colors border border-gray-200 shrink-0" data-id="element-1748">
                    <LogOut size={16} data-id="element-1749" /> Sign Out All Others
                  </button>
                </div>

                <div className="space-y-4" data-id="element-1750">
                  {loginActivities.map(activity => {
                const Icon = activity.icon;
                const isActive = activity.status === 'Active';
                const isFailed = activity.status === 'Failed';
                return <div key={activity.id} className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-xl border transition-colors ${isActive ? 'bg-brand-primary/5 border-brand-primary/20' : 'bg-gray-50 border-gray-100'}`} data-id="element-1751">
                        <div className="flex items-start gap-4 mb-3 sm:mb-0" data-id="element-1752">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isActive ? 'bg-brand-primary/20 text-brand-primary-dark' : isFailed ? 'bg-red-100 text-red-600' : 'bg-white text-gray-600 shadow-sm'}`} data-id="element-1753">
                            <Icon size={20} data-id="element-1754" />
                          </div>
                          <div data-id="element-1755">
                            <div className="flex items-center gap-2" data-id="element-1756">
                              <p className="font-semibold text-brand-text-dark" data-id="element-1757">
                                {activity.device}
                              </p>
                              {isActive && <span className="text-[10px] uppercase tracking-wider font-bold bg-brand-primary text-white px-2 py-0.5 rounded-full" data-id="element-1758">
                                  Current
                                </span>}
                              {isFailed && <span className="text-[10px] uppercase tracking-wider font-bold bg-red-100 text-red-600 px-2 py-0.5 rounded-full" data-id="element-1759">
                                  Failed
                                </span>}
                            </div>
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1 text-xs text-brand-text-light" data-id="element-1760">
                              <span className="flex items-center gap-1" data-id="element-1761">
                                <MapPin size={12} data-id="element-1762" /> {activity.location}
                              </span>
                              <span className="flex items-center gap-1" data-id="element-1763">
                                <Globe size={12} data-id="element-1764" /> {activity.ip}
                              </span>
                              <span className="flex items-center gap-1" data-id="element-1765">
                                <Clock size={12} data-id="element-1766" />{' '}
                                {formatDateTime(activity.date)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>;
              })}
                </div>
              </div>
            </div>}

          {/* Notifications Section */}
          {activeSection === 'notifications' && <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-id="element-1767">
              <h2 className="text-lg font-semibold text-brand-text-dark mb-6" data-id="element-1768">
                Notification Preferences
              </h2>
              <div className="space-y-6 max-w-lg" data-id="element-1769">
                <div className="space-y-4" data-id="element-1770">
                  <h3 className="text-sm font-semibold text-brand-text-dark uppercase tracking-wider" data-id="element-1771">
                    Channels
                  </h3>
                  <div className="flex items-center justify-between py-2" data-id="element-1772">
                    <div data-id="element-1773">
                      <p className="font-medium text-brand-text-dark" data-id="element-1774">
                        Email Notifications
                      </p>
                      <p className="text-sm text-brand-text-light" data-id="element-1775">
                        Receive updates via email
                      </p>
                    </div>
                    <ToggleSwitch enabled={emailNotif} onChange={setEmailNotif} data-id="element-1776" />
                  </div>
                  <div className="flex items-center justify-between py-2" data-id="element-1777">
                    <div data-id="element-1778">
                      <p className="font-medium text-brand-text-dark" data-id="element-1779">
                        SMS Notifications
                      </p>
                      <p className="text-sm text-brand-text-light" data-id="element-1780">
                        Receive updates via text message
                      </p>
                    </div>
                    <ToggleSwitch enabled={smsNotif} onChange={setSmsNotif} data-id="element-1781" />
                  </div>
                  <div className="flex items-center justify-between py-2" data-id="element-1782">
                    <div data-id="element-1783">
                      <p className="font-medium text-brand-text-dark" data-id="element-1784">
                        Push Notifications
                      </p>
                      <p className="text-sm text-brand-text-light" data-id="element-1785">
                        Browser push notifications
                      </p>
                    </div>
                    <ToggleSwitch enabled={pushNotif} onChange={setPushNotif} data-id="element-1786" />
                  </div>
                </div>

                <hr className="border-gray-100" data-id="element-1787" />

                <div className="space-y-4" data-id="element-1788">
                  <h3 className="text-sm font-semibold text-brand-text-dark uppercase tracking-wider" data-id="element-1789">
                    Alert Types
                  </h3>
                  <div className="flex items-center justify-between py-2" data-id="element-1790">
                    <div data-id="element-1791">
                      <p className="font-medium text-brand-text-dark" data-id="element-1792">
                        Transaction Alerts
                      </p>
                      <p className="text-sm text-brand-text-light" data-id="element-1793">
                        Get notified for every transaction
                      </p>
                    </div>
                    <ToggleSwitch enabled={transactionAlerts} onChange={setTransactionAlerts} data-id="element-1794" />
                  </div>
                  <div className="flex items-center justify-between py-2" data-id="element-1795">
                    <div data-id="element-1796">
                      <p className="font-medium text-brand-text-dark" data-id="element-1797">
                        Campaign Updates
                      </p>
                      <p className="text-sm text-brand-text-light" data-id="element-1798">
                        Updates on campaigns you support
                      </p>
                    </div>
                    <ToggleSwitch enabled={campaignUpdates} onChange={setCampaignUpdates} data-id="element-1799" />
                  </div>
                  <div className="flex items-center justify-between py-2" data-id="element-1800">
                    <div data-id="element-1801">
                      <p className="font-medium text-brand-text-dark" data-id="element-1802">
                        Rent Reminders
                      </p>
                      <p className="text-sm text-brand-text-light" data-id="element-1803">
                        Get reminded before rent is due
                      </p>
                    </div>
                    <ToggleSwitch enabled={rentReminders} onChange={setRentReminders} data-id="element-1804" />
                  </div>
                </div>

                <button onClick={() => toast.success('Notification preferences saved')} className="inline-flex items-center gap-2 px-6 py-2.5 bg-brand-primary text-white rounded-xl font-medium hover:bg-brand-primary-medium transition-colors" data-id="element-1805">
                  <Save size={18} data-id="element-1806" /> Save Preferences
                </button>
              </div>
            </div>}

          {/* Bank Accounts Section */}
          {activeSection === 'bank-accounts' && <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-id="element-1807">
              <div className="flex items-center justify-between mb-6" data-id="element-1808">
                <div data-id="element-1809">
                  <h2 className="text-lg font-semibold text-brand-text-dark" data-id="element-1810">
                    Linked Bank Accounts
                  </h2>
                  <p className="text-sm text-brand-text-light mt-1" data-id="element-1811">
                    Manage your linked bank accounts for withdrawals and
                    transfers.
                  </p>
                </div>
                <button onClick={() => setIsAddBankOpen(true)} className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-primary text-white rounded-xl text-sm font-semibold hover:bg-brand-primary-dark transition-colors" data-id="element-1812">
                  <Plus size={16} data-id="element-1813" /> Add Account
                </button>
              </div>
              <div className="space-y-4 max-w-lg" data-id="element-1814">
                {bankAccounts.map((account, index) => <div key={account.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-100 hover:border-gray-200 transition-colors" data-id="element-1815">
                    <div className="flex items-center gap-3" data-id="element-1816">
                      <div className="w-10 h-10 bg-brand-primary/10 rounded-full flex items-center justify-center text-brand-primary" data-id="element-1817">
                        <CreditCard size={20} data-id="element-1818" />
                      </div>
                      <div data-id="element-1819">
                        <p className="font-medium text-brand-text-dark" data-id="element-1820">
                          {account.bank}
                        </p>
                        <p className="text-sm text-brand-text-light" data-id="element-1821">
                          {account.accountNumber} — {account.accountName}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2" data-id="element-1822">
                      {index === 0 && <span className="text-xs bg-brand-primary/10 text-brand-primary px-2 py-1 rounded-full font-medium" data-id="element-1823">
                          Primary
                        </span>}
                      <button onClick={() => handleRemoveBankAccount(account.id)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" title="Remove account" data-id="element-1824">
                        <Trash2 size={16} data-id="element-1825" />
                      </button>
                    </div>
                  </div>)}

                {bankAccounts.length === 0 && <div className="text-center py-8 text-brand-text-light" data-id="element-1826">
                    <CreditCard size={32} className="mx-auto mb-3 opacity-40" data-id="element-1827" />
                    <p className="text-sm" data-id="element-1828">No bank accounts linked yet.</p>
                    <p className="text-xs mt-1" data-id="element-1829">
                      Add a bank account to enable withdrawals.
                    </p>
                  </div>}
              </div>

              {/* Add Bank Account Modal */}
              <Modal isOpen={isAddBankOpen} onClose={() => {
            setIsAddBankOpen(false);
            setNewBankName('');
            setNewBankAccountNumber('');
            setNewBankAccountName('');
          }} title="Add Bank Account" maxWidth="max-w-md" data-id="element-1830">
                <form onSubmit={handleAddBankAccount} className="space-y-5" data-id="element-1831">
                  <div data-id="element-1832">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1833">
                      Bank Name
                    </label>
                    <select value={newBankName} onChange={e => setNewBankName(e.target.value)} className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none bg-white" required data-id="element-1834">
                      <option value="" data-id="element-1835">Select a bank</option>
                      <option value="Access Bank" data-id="element-1836">Access Bank</option>
                      <option value="Ecobank" data-id="element-1837">Ecobank</option>
                      <option value="Fidelity Bank" data-id="element-1838">Fidelity Bank</option>
                      <option value="First Bank" data-id="element-1839">First Bank of Nigeria</option>
                      <option value="FCMB" data-id="element-1840">First City Monument Bank</option>
                      <option value="GTBank" data-id="element-1841">Guaranty Trust Bank</option>
                      <option value="Heritage Bank" data-id="element-1842">Heritage Bank</option>
                      <option value="Keystone Bank" data-id="element-1843">Keystone Bank</option>
                      <option value="Kuda Bank" data-id="element-1844">Kuda Bank</option>
                      <option value="Opay" data-id="element-1845">OPay</option>
                      <option value="Palmpay" data-id="element-1846">PalmPay</option>
                      <option value="Polaris Bank" data-id="element-1847">Polaris Bank</option>
                      <option value="Stanbic IBTC" data-id="element-1848">Stanbic IBTC</option>
                      <option value="Sterling Bank" data-id="element-1849">Sterling Bank</option>
                      <option value="UBA" data-id="element-1850">United Bank for Africa</option>
                      <option value="Union Bank" data-id="element-1851">Union Bank</option>
                      <option value="Unity Bank" data-id="element-1852">Unity Bank</option>
                      <option value="Wema Bank" data-id="element-1853">Wema Bank</option>
                      <option value="Zenith Bank" data-id="element-1854">Zenith Bank</option>
                    </select>
                  </div>
                  <div data-id="element-1855">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1856">
                      Account Number
                    </label>
                    <input type="text" maxLength={10} value={newBankAccountNumber} onChange={e => setNewBankAccountNumber(e.target.value.replace(/\D/g, ''))} placeholder="Enter 10-digit account number" className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none font-mono tracking-wider" required data-id="element-1857" />
                  </div>
                  <div data-id="element-1858">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1859">
                      Account Name
                    </label>
                    <input type="text" value={newBankAccountName} onChange={e => setNewBankAccountName(e.target.value)} placeholder="Enter account holder name" className="w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none" required data-id="element-1860" />
                    <p className="text-xs text-brand-text-light mt-1.5" data-id="element-1861">
                      Please ensure the account name matches your bank records.
                    </p>
                  </div>
                  <div className="flex gap-3 pt-2" data-id="element-1862">
                    <button type="button" onClick={() => {
                  setIsAddBankOpen(false);
                  setNewBankName('');
                  setNewBankAccountNumber('');
                  setNewBankAccountName('');
                }} className="flex-1 py-2.5 border border-gray-200 rounded-xl font-medium text-brand-text-dark hover:bg-gray-50 transition-colors" data-id="element-1863">
                      Cancel
                    </button>
                    <button type="submit" className="flex-1 py-2.5 bg-brand-primary text-white rounded-xl font-semibold hover:bg-brand-primary-dark transition-colors" data-id="element-1864">
                      Add Account
                    </button>
                  </div>
                </form>
              </Modal>
            </div>}

          {/* Data Export Section */}
          {activeSection === 'data' && <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-id="element-1865">
              <h2 className="text-lg font-semibold text-brand-text-dark mb-2" data-id="element-1866">
                Data & Export
              </h2>
              <p className="text-brand-text-light mb-6" data-id="element-1867">
                Download a copy of all your Billvast data as a PDF document.
              </p>
              <div className="space-y-4 max-w-lg" data-id="element-1868">
                <div className="p-4 bg-gray-50 rounded-xl" data-id="element-1869">
                  <h3 className="font-medium text-brand-text-dark mb-1" data-id="element-1870">
                    Export All Data (PDF)
                  </h3>
                  <p className="text-sm text-brand-text-light mb-3" data-id="element-1871">
                    Includes your profile, transactions, campaigns, transfers,
                    and property data in a formatted PDF document.
                  </p>
                  <button onClick={handleExportData} className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-primary text-white rounded-xl font-medium hover:bg-brand-primary-medium transition-colors" data-id="element-1872">
                    <Download size={18} data-id="element-1873" /> Download PDF
                  </button>
                </div>

                <div className="p-4 bg-red-50 rounded-xl" data-id="element-1874">
                  <h3 className="font-medium text-brand-error mb-1" data-id="element-1875">
                    Delete Account
                  </h3>
                  <p className="text-sm text-brand-text-light mb-3" data-id="element-1876">
                    Permanently delete your account and all associated data.
                    This action cannot be undone.
                  </p>
                  <button onClick={() => toast.error('Account deletion is disabled in demo mode')} className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-error text-white rounded-xl font-medium hover:bg-red-600 transition-colors" data-id="element-1877">
                    Delete Account
                  </button>
                </div>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}